import React, { useState } from "react";
import { useData, save } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { PageHeader, DataTable, StatusBadge, Button, Modal, Field, Input, Select } from "../components/shared";
import { Plus, ArrowUp, ArrowDown, Trash2 } from "lucide-react";

export default function ProcessMaster() {
  const { data, loading, reload } = useData("/processes");
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [form, setForm] = useState(null);

  const rows = (data || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  const openAdd = () => setForm({ name: "", mandatory: false, inhouse_allowed: true, jobber_allowed: true, active: true });
  const openEdit = (r) => setForm({ ...r });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Process name required"); return; }
    try {
      await save(form.id ? "put" : "post", form.id ? `/processes/${form.id}` : "/processes", form);
      toast.success(form.id ? "Process updated" : "Process added"); setForm(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete process "${r.name}"?`)) return;
    try { await api.delete(`/processes/${r.id}`); toast.success("Process deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const move = async (idx, dir) => {
    const arr = rows.map((r) => r.id);
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.post("/processes/reorder", { ids: arr }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reorder failed"); }
  };

  const columns = [
    { key: "sequence", header: "Seq", align: "right", render: (r, i) => (
      <div className="flex items-center gap-1 justify-end">
        <span className="tabular">{r.sequence}</span>
        {isAdmin && (
          <span className="flex flex-col">
            <button onClick={() => move(rows.indexOf(r), -1)} className="text-slate-400 hover:text-blue-700" data-testid={`proc-up-${r.id}`}><ArrowUp size={12} /></button>
            <button onClick={() => move(rows.indexOf(r), 1)} className="text-slate-400 hover:text-blue-700" data-testid={`proc-down-${r.id}`}><ArrowDown size={12} /></button>
          </span>
        )}
      </div>
    ) },
    { key: "name", header: "Process", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
    { key: "mandatory", header: "Type", render: (r) => <StatusBadge status={r.mandatory ? "Mandatory" : "Optional"} /> },
    { key: "inhouse_allowed", header: "In-House", render: (r) => (r.inhouse_allowed ? "Yes" : "No") },
    { key: "jobber_allowed", header: "Jobber", render: (r) => (r.jobber_allowed ? "Yes" : "No") },
    { key: "active", header: "Status", render: (r) => <StatusBadge status={r.active === false ? "Inactive" : "Active"} /> },
    ...(isAdmin ? [{ key: "act", header: "Actions", sortable: false, render: (r) => (
      <div className="flex items-center gap-1">
        <button onClick={() => openEdit(r)} className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900" data-testid={`proc-edit-${r.id}`}>Edit</button>
        <button onClick={() => del(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`proc-delete-${r.id}`}><Trash2 size={15} /></button>
      </div>
    ) }] : []),
  ];

  return (
    <div>
      <PageHeader title="Process Master" subtitle="Production process sequence & rules — drives the route on new production orders">
        {isAdmin && <Button onClick={openAdd} data-testid="add-process-btn"><Plus size={15} /> Add Process</Button>}
      </PageHeader>
      {!isAdmin && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">Read-only — Admin access required to edit processes.</p>}
      {!loading && <DataTable testid="process-table" columns={columns} rows={rows} exportName="processes.csv" />}

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Edit Process" : "Add Process"}>
        {form && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Process Name" required className="col-span-2"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="proc-name" placeholder="e.g. Kaja Button" /></Field>
            <Field label="Type"><Select value={form.mandatory ? "1" : "0"} onChange={(e) => setForm({ ...form, mandatory: e.target.value === "1" })} options={[{ value: "0", label: "Optional" }, { value: "1", label: "Mandatory" }]} data-testid="proc-mandatory" /></Field>
            <Field label="Active"><Select value={form.active === false ? "0" : "1"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })} options={[{ value: "1", label: "Active" }, { value: "0", label: "Inactive" }]} /></Field>
            <Field label="In-House Allowed"><Select value={form.inhouse_allowed ? "1" : "0"} onChange={(e) => setForm({ ...form, inhouse_allowed: e.target.value === "1" })} options={[{ value: "1", label: "Yes" }, { value: "0", label: "No" }]} /></Field>
            <Field label="Jobber Allowed"><Select value={form.jobber_allowed ? "1" : "0"} onChange={(e) => setForm({ ...form, jobber_allowed: e.target.value === "1" })} options={[{ value: "1", label: "Yes" }, { value: "0", label: "No" }]} /></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setForm(null)}>Cancel</Button>
              <Button type="submit" data-testid="proc-save">{form.id ? "Save Changes" : "Add Process"}</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
